import {useState, useEffect, useMemo} from "react";
import {useErrors} from "base/error/provider";
import {PlantationService} from "plantation/service";
import {useNavigateWithReload} from "base/navigation/hooks";

const usePlantationFieldStatus = plantation => {
    const [fieldStatus, setFieldStatus] = useState(null);
    const [isForcedImportEnabled, setIsForcedImportEnabled] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [merginData, setMerginData] = useState(null);
    const {handleErrors, clearErrors} = useErrors();

    const navigate = useNavigateWithReload();

    useEffect(() => {
        if (plantation) {
            setIsLoading(true);
            Promise.all([
                PlantationService.getFieldStatus(plantation.id),
                PlantationService.getFieldPlantation(plantation.id).catch(() => null),
            ])
                .then(([statusData, merginFieldData]) => {
                    setFieldStatus(statusData);
                    setMerginData(merginFieldData);
                    clearErrors();
                })
                .catch(error => handleErrors(error))
                .finally(() => setIsLoading(false));
        }
    }, [plantation?.id]);

    const fetchMerginData = () => {
        if (plantation) {
            PlantationService.getFieldPlantation(plantation.id)
                .then(data => {
                    setMerginData(data);
                    clearErrors();
                })
                .catch(error => {
                    setMerginData(null);
                    handleErrors(error);
                });
        }
    };

    const handleExportSubmit = () => {
        return PlantationService.fieldExport(plantation.id)
            .then(data => {
                clearErrors();
                setFieldStatus(data);
                setMerginData(null);
                navigate("", true);
            })
            .catch(error => handleErrors(error))
            .finally(() => setIsDialogOpen(false));
    };

    const handleForcedImportSubmit = () => {
        return PlantationService.fieldImport(plantation.id, true)
            .then(data => {
                clearErrors();
                setFieldStatus(data);
                setMerginData(null);
                navigate("", true);
            })
            .catch(error => handleErrors(error))
            .finally(() => setIsDialogOpen(false));
    };

    const handleImportSubmit = () => {
        return PlantationService.fieldImport(plantation.id)
            .then(data => {
                clearErrors();
                setFieldStatus(data);
                setMerginData(null);
                navigate("", true);
            })
            .catch(error => handleErrors(error))
            .finally(() => setIsDialogOpen(false));
    };

    const enableForcedImport = code => {
        setIsForcedImportEnabled(code === plantation.code);
    };

    const nonRevertibleStatuses = useMemo(
        () => [
            "exported_not_changed",
            "exported_and_changed",
            "imported",
            "forced_imported",
        ],
        []
    );
    const isProcessRevertible = useMemo(
        () => !nonRevertibleStatuses.includes(fieldStatus),
        [nonRevertibleStatuses, fieldStatus]
    );

    return {
        fieldStatus: fieldStatus,
        forcedImport: isForcedImportEnabled,
        handleExportSubmit,
        handleForcedImportSubmit,
        handleImportSubmit,
        enableForcedImport,
        setIsDialogOpen,
        isDialogOpen,
        isProcessRevertible,
        isLoading,
        merginData,
        fetchMerginData,
    };
};

export {usePlantationFieldStatus};
